import * as React from 'react';
import type { JSX } from 'react';

import { TextField, TextFieldProps } from '../../TextField';
import { FieldConverter } from './FieldConverter';

export type BaseFieldProps<TValue> = Omit<TextFieldProps, 'onChange'> & {
    value: TValue;

    onChange: (value: TValue) => void;

    converter: FieldConverter<TValue>;
};

function BaseFieldInner<TValue>(props: BaseFieldProps<TValue>, ref: React.ForwardedRef<HTMLDivElement>): JSX.Element {
    const { onBlur, type, value, onFocus, onChange, error, helperText, required, converter, ...rest } = props;
    const { formatValue, parseValue } = converter;
    const [innerText, setInnerText] = React.useState<string>(formatValue(value));
    const [isFocused, setIsFocused] = React.useState<boolean>(false);
    const parsed = parseValue(innerText);
    // The text can't be read and the field reported the converter's stand-in value for it. Keep the text and its
    // error on screen until the value changes from outside, rather than showing the stand-in.
    const keepsUnreadableText = !parsed.ok && 'value' in parsed && parsed.value === value;

    React.useEffect(() => {
        if (isFocused || keepsUnreadableText) {
            // While the field is focused, we don't want the UI to unpredictable
            // change in front of the user
        } else {
            // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
            setInnerText(formatValue(value));
        }
    }, [value, formatValue, isFocused, keepsUnreadableText]);

    return (
        <TextField
            ref={ref}
            type={type}
            value={innerText}
            required={required}
            onFocus={(e) => {
                setIsFocused(true);
                onFocus && onFocus(e);
            }}
            onBlur={(e) => {
                if (!keepsUnreadableText) {
                    setInnerText(formatValue(value));
                }
                setIsFocused(false);
                onBlur && onBlur(e);
            }}
            onChange={(e) => {
                const newText = e.target.value;
                setInnerText(newText);

                const newParsed = parseValue(newText);
                if (newParsed.ok || 'value' in newParsed) {
                    onChange(newParsed.value);
                }
            }}
            error={!parsed.ok || error}
            helperText={!parsed.ok && parsed.message ? parsed.message : helperText}
            {...rest}
        />
    );
}

export const BaseField = React.forwardRef(BaseFieldInner) as <TValue>(
    props: BaseFieldProps<TValue> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => JSX.Element;
